import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
} from "react";
import {
  ChannelList,
  CpBasic,
  ErrorProp,
  ImageData,
  imageOption,
  UploadedVideoDetails,
} from "../utils/typeConstants";

export interface CpEditContentType {
  contentId: number;
  contentTypeId: number;
  images: any[];
  title: string;
  description: string;
  languageId: number;
  imageId?: number[];
  state?: number;
  keywords?: number[];
  cpVideoIds?: number[];
  channel?: string | null;
}

interface CloudPContentContextType {
  inputRef: React.MutableRefObject<any>;
  customImgRef: React.MutableRefObject<any>;
  fileName: string;
  imgFileName: string;
  setImgFileName: React.Dispatch<React.SetStateAction<string>>;
  setFileName: React.Dispatch<React.SetStateAction<string>>;
  playbackUrl: string;
  setPlaybackURL: React.Dispatch<React.SetStateAction<string>>;
  progressPercentage: React.MutableRefObject<number>;
  playerDuration: number;
  playerCurrentTime: number;
  setPlayerDuration: React.Dispatch<React.SetStateAction<number>>;
  setPlayerCurrentTime: React.Dispatch<React.SetStateAction<number>>;
  playing: boolean;
  setPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  isTranscoding: boolean;
  isUploading: boolean;
  isTranscoded: boolean;
  setIsTranscoding: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTranscoded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  fileData: any;
  setFileData: React.Dispatch<React.SetStateAction<any>>;
  isProgressing: boolean;
  isSystemGenerated: boolean;
  setIsSystemGenerated: React.Dispatch<React.SetStateAction<boolean>>;
  setIsProgressing: React.Dispatch<React.SetStateAction<boolean>>;
  selectedThumnailId: number | null;
  setSelectedThumbnailId: React.Dispatch<React.SetStateAction<number | null>>;
  isImageProgressing: boolean;
  setIsImageProgressing: React.Dispatch<React.SetStateAction<boolean>>;
  setCpBasic: React.Dispatch<React.SetStateAction<CpBasic>>;
  cpBasic: CpBasic;

  presetImageData: ImageData[];
  setPresetImageData: React.Dispatch<React.SetStateAction<ImageData[]>>;
  addPresetImageData: (newImageData: ImageData) => void;
  videoPresetIds: string[];
  setVideoPresetIds: React.Dispatch<React.SetStateAction<string[]>>;
  isUploaded: boolean;
  setIsUploaded: React.Dispatch<React.SetStateAction<boolean>>;
  isDisabled: boolean;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  imageOptions: imageOption[];
  setImageOptions: React.Dispatch<React.SetStateAction<imageOption[]>>;
  customImage: string | null;
  setCustomImage: React.Dispatch<React.SetStateAction<string | null>>;
  contentTypeId: number | null;
  setContentTypeId: React.Dispatch<React.SetStateAction<number | null>>;
  editCpContentId: number | undefined;
  setEditCpContentId: React.Dispatch<React.SetStateAction<number | undefined>>;
  cpEditContent: CpEditContentType | null;
  setCpEditContent: React.Dispatch<
    React.SetStateAction<CpEditContentType | null>
  >;
  intialEditContent: CpEditContentType | null;
  setInitialEditContent: React.Dispatch<
    React.SetStateAction<CpEditContentType | null>
  >;
  imagePresetIds: number[];
  setImagePresetIds: React.Dispatch<React.SetStateAction<number[]>>;
  setErrorContentMessage: React.Dispatch<
    React.SetStateAction<ErrorProp | null>
  >;
  errorContentMessage: ErrorProp | null;
  setAssertId: React.Dispatch<React.SetStateAction<string>>;
  assertId: string;
  selectedCustomImgId: number | null;
  setSelectedCustomImgId: React.Dispatch<React.SetStateAction<number | null>>;
  cpVideoId: number[];
  setCpVideoId: React.Dispatch<React.SetStateAction<number[]>>;
  isSaveState: boolean;
  setIsSaveState: React.Dispatch<React.SetStateAction<boolean>>;
  editCpVideoDetails: UploadedVideoDetails[] | null;
  setEditCpVideoDetails: React.Dispatch<
    React.SetStateAction<UploadedVideoDetails[] | null>
  >;
  contentIdList: string;
  setContentIdList: React.Dispatch<React.SetStateAction<string>>;
  isStatusTranscode: boolean;
  setIsStatusTranscode: React.Dispatch<React.SetStateAction<boolean>>;
  isTranscodingJobs: boolean;
  setIsTranscodingJobs: React.Dispatch<React.SetStateAction<boolean>>;
  isJobFailed: boolean;
  setIsJobFailed: React.Dispatch<React.SetStateAction<boolean>>;
  isJobsRequired: boolean;
  setIsJobsRequired: React.Dispatch<React.SetStateAction<boolean>>;
  currentTab: number;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
  channelItem: ChannelList | null;
  setChannelItem: React.Dispatch<React.SetStateAction<ChannelList | null>>;
  filters: {
    contentType?: string;
    language?: string;
    keywords?: string;
    title?: string;
    toDate?: string;
    fromDate?: string;
  };
  setFilters: React.Dispatch<
    React.SetStateAction<{
      contentType?: string;
      language?: string;
      keywords?: string;
      title?: string;
      toDate?: string;
      fromDate?: string;
    }>
  >;
  contentType: string;
  setContentType: React.Dispatch<React.SetStateAction<string>>;
  isFilter: boolean;
  setIsFilter: React.Dispatch<React.SetStateAction<boolean>>;
  isSuccess: boolean;
  setIsSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  isError: boolean;
  setIsError: React.Dispatch<React.SetStateAction<boolean>>;
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  openEmbededDrawer: boolean;
  setOpenEmbededDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  embededPlayer: number | null;
  setEmbededPlayer: React.Dispatch<React.SetStateAction<number | null>>;
  embededCode: string | null;
  setEmbededCode: React.Dispatch<React.SetStateAction<string | null>>;
}

const CloudPContext = createContext<CloudPContentContextType>({
  inputRef: React.createRef(),
  customImgRef: React.createRef(),
  fileName: "",
  setFileName: () => {},
  imgFileName: "",
  setImgFileName: () => {},
  playbackUrl: "",
  setPlaybackURL: () => {},
  progressPercentage: { current: 0 },
  playerCurrentTime: 0,
  setPlayerCurrentTime: () => {},
  playerDuration: 0,
  setPlayerDuration: () => {},
  playing: false,
  setPlaying: () => {},
  isTranscoding: false,
  isUploading: false,
  setIsTranscoding: () => {},
  setIsUploading: () => {},
  isTranscoded: false,
  setIsTranscoded: () => {},
  fileData: null,
  setFileData: () => {},
  isProgressing: false,
  setIsProgressing: () => {},
  isSystemGenerated: true,
  setIsSystemGenerated: () => {},
  selectedThumnailId: null,
  setSelectedThumbnailId: () => {},
  isImageProgressing: false,
  setIsImageProgressing: () => {},
  cpBasic: {},
  setCpBasic: () => {},

  presetImageData: [],
  addPresetImageData: () => {},
  videoPresetIds: [],
  setVideoPresetIds: () => {},
  isUploaded: false,
  setIsUploaded: () => {},
  isDisabled: false,
  setIsDisabled:()=>{},
  imageOptions: [],
  setImageOptions: () => {},
  customImage: null,
  setCustomImage: () => {},
  contentTypeId: null,
  setContentTypeId: () => {},
  editCpContentId: 0,
  setEditCpContentId: () => {},
  cpEditContent: null,
  setCpEditContent: () => {},
  imagePresetIds: [],
  setImagePresetIds: () => {},
  setPresetImageData: () => {},
  setErrorContentMessage: () => {},
  errorContentMessage: null,
  setAssertId: () => {},
  assertId: "",
  selectedCustomImgId: null,
  setSelectedCustomImgId: () => {},
  cpVideoId: [],
  setCpVideoId: () => {},
  isSaveState: false,
  setIsSaveState: () => {},
  editCpVideoDetails: null,
  setEditCpVideoDetails: () => {},
  contentIdList: "",
  setContentIdList: () => {},
  isStatusTranscode: true,
  setIsStatusTranscode: () => {},
  isTranscodingJobs: false,
  setIsTranscodingJobs: () => {},
  isJobFailed: false,
  setIsJobFailed: () => {},
  intialEditContent: null,
  setInitialEditContent: () => {},
  isJobsRequired: true,
  setIsJobsRequired: () => {},
  currentTab: 0,
  channelItem: null,
  setChannelItem: () => {},
  setCurrentTab: () => {},
  filters: { contentType: "", language: "", keywords: "", title: "" },
  setFilters: () => {},
  contentType: "",
  setContentType: () => {},
  isFilter: false,
  setIsFilter: () => {},
  isSuccess: false,
  setIsSuccess: () => {},
  isError: false,
  setIsError: () => {},
  message: "",
  setMessage: () => {},
  openEmbededDrawer: false,
  setOpenEmbededDrawer: () => {},
  embededPlayer: null,
  setEmbededPlayer: () => {},
  embededCode: null,
  setEmbededCode: () => {},
});

interface CloudPContentProviderProps {
  children: ReactNode;
}

export const CloudPContentProvider: React.FC<CloudPContentProviderProps> = ({
  children,
}) => {
  const [fileName, setFileName] = useState<string>("");
  const [imgFileName, setImgFileName] = useState<string>("");
  const [playbackUrl, setPlaybackURL] = useState<string>("");
  const inputRef = useRef<any>(null);
  const customImgRef = useRef<any>(null);
  const progressPercentage = useRef<number>(30);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [playerCurrentTime, setPlayerCurrentTime] = useState(0);
  const [isTranscoding, setIsTranscoding] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isTranscoded, setIsTranscoded] = useState<boolean>(false);
  const [isUploaded, setIsUploaded] = useState<boolean>(false);
  const[isDisabled,setIsDisabled]= useState<boolean>(false);
  const [isProgressing, setIsProgressing] = useState<boolean>(false);
  const [isSystemGenerated, setIsSystemGenerated] = useState<boolean>(true);
  const [isImageProgressing, setIsImageProgressing] = useState<boolean>(true);
  const [imageOptions, setImageOptions] = useState<imageOption[]>([]);
  const [playing, setPlaying] = useState(false);
  const [fileData, setFileData] = useState<any>(null);
  const [selectedThumnailId, setSelectedThumbnailId] = useState<number | null>(
    null
  );
  const [selectedCustomImgId, setSelectedCustomImgId] = useState<number | null>(
    null
  );
  const [isTranscodingJobs, setIsTranscodingJobs] = useState<boolean>(false);
  const [contentTypeId, setContentTypeId] = useState<number | null>(null);
  const [customImage, setCustomImage] = useState<string | null>(null);
  const [editCpContentId, setEditCpContentId] = useState<number | undefined>(0);
  const [cpVideoId, setCpVideoId] = useState<number[]>([]);
  const [cpBasic, setCpBasic] = useState<CpBasic>({});
  const [presetImageData, setPresetImageData] = useState<ImageData[]>([]);
  const [imagePresetIds, setImagePresetIds] = useState<number[]>([]);
  const [channelItem, setChannelItem] = useState<ChannelList | null>(null);
  const [isSaveState, setIsSaveState] = useState<boolean>(false);
  const [isJobsRequired, setIsJobsRequired] = useState<boolean>(true);
  const [contentIdList, setContentIdList] = useState<string>("");
  const [currentTab, setCurrentTab] = useState(0);
  const [filters, setFilters] = useState<{
    contentType?: string;
    language?: string;
    keywords?: string;
    title?: string;
    toDate?: string;
    fromDate?: string;
  }>({});
  const [contentType, setContentType] = useState<string>("");
  const [isFilter, setIsFilter] = useState<boolean>(false);

  const [editCpVideoDetails, setEditCpVideoDetails] = useState<
    UploadedVideoDetails[] | null
  >(null);

  const addPresetImageData = (newImageData: ImageData) => {
    setPresetImageData((prevData) => {
      return [...prevData, newImageData];
    });
  };
  const [videoPresetIds, setVideoPresetIds] = useState<string[]>([]);
  const [cpEditContent, setCpEditContent] = useState<CpEditContentType | null>(
    null
  );
  const [intialEditContent, setInitialEditContent] =
    useState<CpEditContentType | null>(null);

  const [errorContentMessage, setErrorContentMessage] =
    useState<ErrorProp | null>({
      title: "",
      description: "",
      flag: false,
    });
  const [assertId, setAssertId] = useState("");
  const [isStatusTranscode, setIsStatusTranscode] = useState<boolean>(true);
  const [isJobFailed, setIsJobFailed] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [openEmbededDrawer, setOpenEmbededDrawer] = useState<boolean>(false);
  const [embededPlayer, setEmbededPlayer] = useState<number | null>(null);
  const [message, setMessage] = useState<string>("");
  const [embededCode, setEmbededCode] = useState<string | null>("");

  return (
    <CloudPContext.Provider
      value={{
        inputRef,
        fileName,
        setFileName,
        playbackUrl,
        setPlaybackURL,
        progressPercentage,
        playerCurrentTime,
        setPlayerCurrentTime,
        playerDuration,
        setPlayerDuration,
        playing,
        setPlaying,
        isUploading,
        setIsUploading,
        isTranscoding,
        setIsTranscoding,
        isTranscoded,
        setIsTranscoded,
        fileData,
        setFileData,
        isProgressing,
        setIsProgressing,
        isSystemGenerated,
        setIsSystemGenerated,
        selectedThumnailId,
        setSelectedThumbnailId,
        customImgRef,
        isImageProgressing,
        setIsImageProgressing,
        setCpBasic,
        cpBasic,
        addPresetImageData,
        presetImageData,
        videoPresetIds,
        setVideoPresetIds,
        isUploaded,
        setIsUploaded,
        isDisabled,
       setIsDisabled,
        imageOptions,
        setImageOptions,
        customImage,
        setCustomImage,
        contentTypeId,
        setContentTypeId,
        editCpContentId,
        setEditCpContentId,
        cpEditContent,
        setCpEditContent,
        imagePresetIds,
        setImagePresetIds,
        setPresetImageData,
        setErrorContentMessage,
        errorContentMessage,
        setAssertId,
        assertId,
        selectedCustomImgId,
        setSelectedCustomImgId,
        cpVideoId,
        setCpVideoId,
        isSaveState,
        setIsSaveState,
        editCpVideoDetails,
        setEditCpVideoDetails,
        contentIdList,
        setContentIdList,
        isStatusTranscode,
        setIsStatusTranscode,
        isTranscodingJobs,
        setIsTranscodingJobs,
        isJobFailed,
        setIsJobFailed,
        imgFileName,
        setImgFileName,
        intialEditContent,
        setInitialEditContent,
        isJobsRequired,
        setIsJobsRequired,
        currentTab,
        setCurrentTab,
        setChannelItem,
        channelItem,
        filters,
        setFilters,
        contentType,
        setContentType,
        isFilter,
        setIsFilter,
        isSuccess,
        setIsSuccess,
        isError,
        setIsError,
        message,
        setMessage,
        openEmbededDrawer,
        setOpenEmbededDrawer,
        embededPlayer,
        setEmbededPlayer,
        embededCode,
        setEmbededCode,
      }}
    >
      {children}
    </CloudPContext.Provider>
  );
};

export const useCloudPContent = () => useContext(CloudPContext);
